import React from "react";
import { EEG_SERVICES, INSURANCE_AGREEMENT_ID } from "../../utils/constants";
import { getStore } from "../../store/store";
import { InvoiceAlertDialog, InvoiceAlert } from "./InvoiceAlertDialog";
import { api } from "../../api/config";
import { queryClient } from "../../main";
import { calculateApiItemsTotal } from "./utils";
import { RecordContextType } from "../../components/record/Record";
import { formatCurrency } from "../../utils/formatCurrency";

const INVOICED_TOLERANCE = 0.01;

export const hasAgreementId6 = (orderItems: Array<any>) => {
    const hasAgreement6 = orderItems.some((item) => item.service_variant?.agreement?.id === INSURANCE_AGREEMENT_ID);
    return hasAgreement6;
};

export const hasOnlyEegServices = (orderItems: Array<any>) => {
    const allAreEeg = orderItems.every((item) => EEG_SERVICES.includes(item.service_variant?.service?.id));
    return allAreEeg;
};

export const allItemsHaveSameAgreement = (orderItems: Array<any>) => {
    if (orderItems.length === 0) return true;
    const firstAgreementId = orderItems[0]?.service_variant?.agreement?.id;
    const allSame = orderItems.every((item) => item.service_variant?.agreement?.id === firstAgreementId);
    return allSame;
};

export const validateNoExistingInvoice = (invoiceId: any) => {
    if (invoiceId) {
        return { isValid: false, errorMessage: "Já existe uma fatura associada a esta compra" };
    }
    return { isValid: true };
};

export const validateNoExistingInvoicing = (schedules: Array<any>, invoicedNet: number) => {
    if (schedules.length > 0) {
        return {
            isValid: false,
            errorMessage: `Esta compra tem ${schedules.length} fatura(s) agendada(s). Criar uma fatura do total iria duplicar a faturação.`,
        };
    }
    if (invoicedNet > INVOICED_TOLERANCE) {
        return {
            isValid: false,
            errorMessage: `Já existem documentos de faturação no valor de ${formatCurrency(invoicedNet)} para esta compra.`,
        };
    }
    return { isValid: true };
};

export const validateOrderItemsAgreement = (orderItems: Array<any>) => {
    const allSameAgreement = allItemsHaveSameAgreement(orderItems);
    if (!allSameAgreement) {
        return { isValid: false, errorMessage: "Todos os itens da encomenda devem ter o mesmo acordo" };
    }
    return { isValid: true };
};

type BuildInvoiceAlertsParams = {
    insuranceNumber: string;
    orderItems: Array<any>;
    priceNotes: string;
    nextStep: string;
};

export const buildInvoiceAlerts = ({ insuranceNumber, orderItems, priceNotes, nextStep }: BuildInvoiceAlertsParams) => {
    const alerts: Array<InvoiceAlert> = [];

    const shouldShowInsuranceAlert = insuranceNumber && hasAgreementId6(orderItems) && !hasOnlyEegServices(orderItems);
    if (shouldShowInsuranceAlert) {
        alerts.push({
            title: "Seguro",
            lines: [
                insuranceNumber,
                "Verificar se é para tirar faturas individuais",
            ],
        });
    }

    const hasPriceNotes = priceNotes?.trim();
    if (hasPriceNotes) {
        alerts.push({
            title: "Preço especial",
            lines: [
                `"${priceNotes}"`,
            ],
        });
    }

    const hasNextStep = nextStep?.trim();
    if (hasNextStep) {
        alerts.push({
            title: "Próximo passo",
            lines: [
                `"${nextStep}"`,
            ],
        });
    }

    return alerts;
};

export const handleInvoiceAlert = (alerts: Array<InvoiceAlert>): Promise<boolean> => {
    const { appDialog } = getStore();

    const userChoicePromise = new Promise<boolean>((resolve) => {
        appDialog.createDialog({
            title: "",
            contentAndActions: React.createElement(InvoiceAlertDialog, {
                alerts: alerts,
                onConfirm: () => {
                    appDialog.toggleIsOpen();
                    resolve(true);
                },
                onCancel: () => {
                    appDialog.toggleIsOpen();
                    resolve(false);
                },
            }),
            allowSoftCancel: true,
            onCancel: () => {
                resolve(false);
            },
        });
    });

    return userChoicePromise;
};

export const submitInvoice = async (context: RecordContextType, withoutNif: boolean, cachedUser: any) => {
    const { notifications } = getStore();
    const orderTotalAmount = calculateApiItemsTotal(context.item.order_items);

    const orderData = {
        ...context.item,
        action_done_by: cachedUser,
        total: orderTotalAmount,
    };

    await api({
        method: "POST",
        url: "https://hook.eu1.make.com/km0d1rbb97nm1jvocargtfpqyxbkmd8l",
        data: { withoutNif, order: orderData, isCorrection: false },
    });

    queryClient.invalidateQueries({ queryKey: [`orders-${context.item.id}`] });
    queryClient.invalidateQueries({ queryKey: ["order-invoice", context.item.id] });

    const isOrderPaid = context.item.payment_status === "paid";
    const confirmationMessage = isOrderPaid
        ? "Fatura criada com sucesso e enviada para o email do cliente"
        : "Fatura criada com sucesso e enviada para o email do cliente com link de pagamento";

    notifications.setNotification({ message: confirmationMessage, isOpen: true, severity: "success" });
};
